import { useState, useEffect } from "react";
import { styled } from "styled-components";
import { 
    Typography, 
    Button,
    Box,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
} from "@mui/material";
import { ArrowBack, Add, Check, Search, ExpandLess, Delete } from "@mui/icons-material";
import { DataGridPremium } from "@mui/x-data-grid-premium";
import { useUser } from '../../../contexts/UserContext';

const Container = styled.div`
    min-height: 100vh;
    background: linear-gradient(to bottom right, #fafafa, #f5f5f5);
    padding: 2rem;
`;

const Card = styled.div`
    background: white;
    border-radius: 8px;
    padding: 1.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    margin-bottom: 1.5rem;
`;

const CardHeader = styled.div`
    margin-bottom: 1rem;
    h2 {
        font-size: 1.25rem;
        font-weight: 600;
        margin:0;
        color:rgb(39, 33, 33);
    }
    p {
        font-size: 0.875rem;
        color: #666;
    }
`;

const AddUserPanel = styled.div`
    overflow: hidden;
    max-height: ${props => props.isOpen ? '1000px' : '0'};
    opacity: ${props => props.isOpen ? 1 : 0};
    margin-bottom: ${props => props.isOpen ? '1rem' : '0'};
    transition: max-height 500ms ease-in-out, opacity 500ms ease-in-out, margin-bottom 500ms ease-in-out;
    pointer-events: ${props => props.isOpen ? 'auto' : 'none'};
`;

const AddUserContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 1rem;
    border: 1px solid #e5e5e5;
    border-radius: 0.5rem;
    opacity: ${props => props.isOpen ? 1 : 0};
    transform: ${props => props.isOpen ? 'translateY(0)' : 'translateY(-0.5rem)'};
    transition: opacity 500ms ease-in-out, transform 500ms ease-in-out;
`;

const UserList = styled.div`
    max-height: 300px;
    overflow-y: auto;
    overflow-x: hidden;
    border: 1px solid #e5e5e5;
    border-radius: 4px;
    padding: 0.5rem;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
`;

const UserItem = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.5rem;
    border-radius: 4px;
    cursor: pointer;
    &:hover {
        background-color: rgba(0, 0, 0, 0.05);
    }
`;

const Checkbox = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 16px;
    width: 16px;
    border-radius: 2px;
    border: 1px solid ${props => props.checked ? '#ccc5b9' : '#ccc5b9'};
    background-color: ${props => props.checked ? '#ccc5b9' : 'white'};
    cursor: pointer;
    transition: all 0.2s;
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem;
    color: #666;
    text-align: center;
`;

const EmptyIcon = styled.div`
    width: 4rem;
    height: 4rem;
    border-radius: 50%;
    background-color: rgba(0, 0, 0, 0.05);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1rem;
`;

export function AccessManagement({ onBack }) {
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('manage'); // 'manage' | 'query'
    const [groupsLoading, setGroupsLoading] = useState(true);
    const [permissionsLoading, setPermissionsLoading] = useState(false);
    const [usersLoading, setUsersLoading] = useState(false);
    const [groups, setGroups] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [permissions, setPermissions] = useState([]);
    const [users, setUsers] = useState([]);
    const [availableUsers, setAvailableUsers] = useState([]);
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isAddUserPanelOpen, setIsAddUserPanelOpen] = useState(false);
    const [userSearchQuery, setUserSearchQuery] = useState("");

    // 사용자 권한 조회 상태
    const [querySearch, setQuerySearch] = useState("");
    const [queryAllUsers, setQueryAllUsers] = useState([]);
    const [queryAllUsersLoaded, setQueryAllUsersLoaded] = useState(false);
    const [querySelectedUser, setQuerySelectedUser] = useState(null);
    const [queryUserGroups, setQueryUserGroups] = useState([]);
    const [queryGroupsLoading, setQueryGroupsLoading] = useState(false);
    const [querySelectedGroupId, setQuerySelectedGroupId] = useState(null);
    const [queryPermissions, setQueryPermissions] = useState([]);
    const [queryPermissionsLoading, setQueryPermissionsLoading] = useState(false);

    const { user } = useUser();
    console.log('user', availableUsers);

    // 초기 데이터: 그룹 목록만 불러오기
    useEffect(() => {
        const fetchGroups = async () => {
            try {
                setGroupsLoading(true);

                const groupsRes = await fetch('/api/groups');
                if (!groupsRes.ok) throw new Error("그룹 목록을 가져오는데 실패했습니다.");

                const groupsData = await groupsRes.json();
                setGroups(groupsData.filter(g => g.use_yn !== false) || []);
            } catch (error) {
                console.error("그룹 목록 로드 실패:", error);
                setGroups([]);
            } finally {
                setGroupsLoading(false);
            }
        };

        fetchGroups();
    }, []);

    // 그룹 선택 시 필요한 데이터만 불러오기
    const handleGroupSelect = async (groupId) => {
        setSelectedGroup(groupId);
        setSelectedUserIds([]);
        setIsAddUserPanelOpen(false);
        setUserSearchQuery("");

        try {
            // 권한 정보 로드 (계층 구조 API 사용)
            setPermissionsLoading(true);
            const hierarchyRes = await fetch(`/api/group-resource-hierarchy?groupId=${groupId}`);
            
            if (!hierarchyRes.ok) throw new Error("그룹별 리소스 계층 구조를 가져오는데 실패했습니다.");
            
            const hierarchyData = await hierarchyRes.json();
            console.log('hierarchyData', hierarchyData);
            
            // 계층 구조를 평면 배열로 변환 (resource_nm과 action만 표시)
            const flattenHierarchy = (resources) => {
                const result = [];
                resources.forEach(resource => {
                    // 각 action을 개별 행으로 추가
                    resource.actions.forEach(action => {
                        result.push({
                            id: `${resource.resource_id}_${action}`,
                            resource_id: resource.resource_id,
                            resource_nm: resource.resource_nm,
                            action: action,
                            lvl: resource.lvl,
                            parent_resource_id: resource.parent_resource_id
                        });
                    });
                    
                    // 자식 리소스가 있으면 재귀적으로 추가
                    if (resource.children && resource.children.length > 0) {
                        const children = flattenHierarchy(resource.children);
                        result.push(...children);
                    }
                });
                return result;
            };
            
            const flatPermissions = flattenHierarchy(hierarchyData.resources || []);
            // resource_nm 기준으로 정렬
            flatPermissions.sort((a, b) => {
                return a.resource_nm.localeCompare(b.resource_nm, 'ko');
            });
            setPermissions(flatPermissions);
            setPermissionsLoading(false);
            console.log('flatPermissions', flatPermissions);

            // 사용자 정보 로드 (그룹별 사용자 API 사용)
            setUsersLoading(true);
            const [groupUsersRes, usersRes, userGroupRes] = await Promise.all([
                fetch(`/api/group-users?groupId=${groupId}`),
                fetch('/api/users/all'),
                fetch('/api/user-group-map')
            ]);

            if (!groupUsersRes.ok) throw new Error("그룹별 사용자 목록을 가져오는데 실패했습니다.");
            if (!usersRes.ok) throw new Error("사용자 목록을 가져오는데 실패했습니다.");
            if (!userGroupRes.ok) throw new Error("사용자-그룹 매핑을 가져오는데 실패했습니다.");

            const [groupUsersData, usersData, userGroupData] = await Promise.all([
                groupUsersRes.json(),
                usersRes.json(),
                userGroupRes.json()
            ]);

            // 선택된 그룹의 사용자 목록 (create_dt 포함)
            setUsers(groupUsersData);

            // 사용 가능한 사용자 목록 (이 그룹에 속하지 않은 사용자)
            const currentGroupUserIds = new Set(groupUsersData.map(u => u.user_id));
            const availableUsersList = usersData.filter(u => !currentGroupUserIds.has(u.user_id));
            setAvailableUsers(availableUsersList);
            console.log('availableUsers 설정:', {
                전체사용자수: usersData.length,
                현재그룹사용자수: groupUsersData.length,
                사용가능한사용자수: availableUsersList.length
            });
            
            setUsersLoading(false);
        } catch (error) {
            console.error("그룹 데이터 로드 실패:", error);
            setPermissions([]);
            setUsers([]);
            setAvailableUsers([]);
            setPermissionsLoading(false);
            setUsersLoading(false);
        }
    };

    // 사용자 권한 조회 탭 전환 시 전체 사용자 로드
    const handleTabChange = async (tab) => {
        setActiveTab(tab);
        if (tab === 'query' && !queryAllUsersLoaded) {
            try {
                const res = await fetch('/api/users/all');
                if (res.ok) {
                    const data = await res.json();
                    setQueryAllUsers(data);
                    setQueryAllUsersLoaded(true);
                }
            } catch (e) {
                console.error('사용자 목록 로드 실패:', e);
            }
        }
    };

    const queryFilteredUsers = () => {
        if (!querySearch.trim()) return [];
        const q = querySearch.toLowerCase();
        return queryAllUsers.filter(u =>
            (u.name || '').toLowerCase().includes(q) ||
            (u.user_id || '').toLowerCase().includes(q) ||
            (u.email || '').toLowerCase().includes(q) ||
            (u.org_name || '').toLowerCase().includes(q)
        ).slice(0, 20);
    };

    const handleQuerySelectUser = async (u) => {
        setQuerySelectedUser(u);
        setQueryUserGroups([]);
        setQuerySelectedGroupId(null);
        setQueryPermissions([]);
        try {
            setQueryGroupsLoading(true);
            const res = await fetch('/api/user-group-map');
            if (!res.ok) throw new Error();
            const mappings = await res.json();
            const userGroupIds = new Set(
                mappings.filter(m => m.user_id === u.user_id).map(m => m.group_id)
            );
            setQueryUserGroups(groups.filter(g => userGroupIds.has(g.group_id)));
        } catch (e) {
            console.error('사용자 그룹 로드 실패:', e);
        } finally {
            setQueryGroupsLoading(false);
        }
    };

    const handleQuerySelectGroup = async (groupId) => {
        setQuerySelectedGroupId(groupId);
        setQueryPermissions([]);
        try {
            setQueryPermissionsLoading(true);
            const res = await fetch(`/api/group-resource-hierarchy?groupId=${groupId}`);
            if (!res.ok) throw new Error();
            const data = await res.json();
            const flatten = (resources) => {
                const result = [];
                resources.forEach(r => {
                    r.actions.forEach(action => {
                        result.push({ id: `${r.resource_id}_${action}`, resource_id: r.resource_id, resource_nm: r.resource_nm, action });
                    });
                    if (r.children?.length) result.push(...flatten(r.children));
                });
                return result;
            };
            const flat = flatten(data.resources || []);
            flat.sort((a, b) => a.resource_nm.localeCompare(b.resource_nm, 'ko'));
            setQueryPermissions(flat);
        } catch (e) {
            console.error('권한 로드 실패:', e);
        } finally {
            setQueryPermissionsLoading(false);
        }
    };

    const handleDeleteQueryUserGroup = async (groupId) => {
        if (!window.confirm(`'${querySelectedUser?.name || querySelectedUser?.user_id}' 사용자를 그룹 ${groupId}에서 제거하시겠습니까?`)) return;
        try {
            const res = await fetch('/api/user-group-map', {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ user_id: querySelectedUser.user_id, group_id: groupId })
            });
            if (!res.ok) throw new Error();
            setQueryUserGroups(prev => prev.filter(g => g.group_id !== groupId));
            if (querySelectedGroupId === groupId) {
                setQuerySelectedGroupId(null);
                setQueryPermissions([]);
            }
        } catch (e) {
            console.error('그룹 제거 실패:', e);
            alert('그룹 제거에 실패했습니다.');
        }
    };

    const groupPermissionsByMenu = (perms) => {
        const grouped = {};
        perms.forEach((perm) => {
            const menuId = perm.menu_id || perm.resource_id;
            if (!grouped[menuId]) {
                grouped[menuId] = {
                    menu_id: menuId,
                    menu_nm: perm.menu_nm || menuId,
                    permissions: [],
                };
            }
            grouped[menuId].permissions.push({
                permission_id: perm.permission_id || `${perm.resource_id}_${perm.action}`,
                permission_nm: perm.permission_nm || `${perm.resource_nm} (${perm.action})`,
            });
        });
        return Object.values(grouped);
    };

    const handleAddUser = async () => {
        if (!selectedGroup) {
            alert("그룹을 먼저 선택해주세요.");
            return;
        }
        if (!selectedUserIds || selectedUserIds.length === 0) {
            alert("사용자를 선택해주세요.");
            return;
        }

        try {
            setIsSubmitting(true);

            const addPromises = selectedUserIds.map(async (userId) => {
                const response = await fetch(`/api/user-group-map`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        user_id: userId,
                        group_id: selectedGroup,
                        use_yn: true,
                    }),
                });
                if (!response.ok) {
                    const error = await response.json();
                    throw new Error(error.error || `사용자 ${userId} 추가에 실패했습니다.`);
                }
                return await response.json();
            });

            await Promise.all(addPromises);
            
            const addedCount = selectedUserIds.length;
            setSelectedUserIds([]);
            
            // 그룹 데이터 다시 불러오기 (최신 상태 유지)
            await handleGroupSelect(selectedGroup);
            
            alert(`${addedCount}명의 인원이 추가되었습니다.`);
        } catch (error) {
            console.error("인원 추가 실패:", error);
            alert(error.message || "인원 추가에 실패했습니다.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteUser = async (userId) => {
        if (!selectedGroup) {
            alert("그룹을 먼저 선택해주세요.");
            return;
        }
        if (!window.confirm(`${userId} 사용자를 그룹에서 제거하시겠습니까?`)) {
            return;
        }

        try {
            const response = await fetch(
                `/api/user-group-map`,
                {
                    method: "DELETE",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        user_id: userId,
                        group_id: selectedGroup,
                    }),
                }
            );
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || "사용자 제거에 실패했습니다.");
            }
            
            // 그룹 데이터 다시 불러오기 (최신 상태 유지)
            await handleGroupSelect(selectedGroup);
            
            alert("사용자가 제거되었습니다.");
        } catch (error) {
            console.error("사용자 제거 실패:", error);
            alert(error.message || "사용자 제거에 실패했습니다.");
        }
    };

    const toggleUserSelection = (userId) => {
        setSelectedUserIds((prev) => {
            if (prev.includes(userId)) {
                return prev.filter((id) => id !== userId);
            } else {
                return [...prev, userId];
            }
        });
    };

    const toggleSelectAll = () => {
        const filteredUsers = getFilteredUsers();
        if (selectedUserIds.length === filteredUsers.length && filteredUsers.length > 0) {
            setSelectedUserIds((prev) => prev.filter((id) => !filteredUsers.some((u) => u.user_id === id)));
        } else {
            const filteredIds = filteredUsers.map((u) => u.user_id);
            setSelectedUserIds((prev) => {
                const newIds = [...prev];
                filteredIds.forEach((id) => {
                    if (!newIds.includes(id)) {
                        newIds.push(id);
                    }
                });
                return newIds;
            });
        }
    };

    const getFilteredUsers = () => {
        let filtered = availableUsers;

        if (userSearchQuery.trim()) {
            const query = userSearchQuery.toLowerCase();
            filtered = availableUsers.filter((user) => {
                const name = (user.name || "").toLowerCase();
                const userId = (user.user_id || "").toLowerCase();
                const email = (user.email || "").toLowerCase();
                const positName = (user.posit_name || "").toLowerCase();
                const orgName = (user.org_name || "").toLowerCase();
                return (
                    name.includes(query) ||
                    userId.includes(query) ||
                    email.includes(query) ||
                    positName.includes(query) ||
                    orgName.includes(query)
                );
            });
        }

        return filtered.sort((a, b) => {
            const aSelected = selectedUserIds.includes(a.user_id);
            const bSelected = selectedUserIds.includes(b.user_id);
            if (aSelected && !bSelected) return -1;
            if (!aSelected && bSelected) return 1;
            return 0;
        });
    };

    const formatDate = (dateString) => {
        if (!dateString) return "-";
        const date = new Date(dateString);
        date.setHours(date.getHours() - 9);
        return date.toLocaleDateString("ko-KR", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        });
    };

    // 권한 체크 (필요시 수정)
    if (user && user.menu_access?.accessManagement?.includes && !user.menu_access.accessManagement.includes('read_all')) {
        return (
            <Container>
                <EmptyState>
                    <Typography variant="h6" color="textSecondary">
                        접근 권한이 없습니다.
                    </Typography>
                </EmptyState>
            </Container>
        );
    }

    return (
        <Container>
            {/* 탭 토글 */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '1.5rem' }}>
                {['manage', 'query'].map(tab => (
                    <button
                        key={tab}
                        onClick={() => handleTabChange(tab)}
                        style={{
                            padding: '6px 16px',
                            fontSize: '13px',
                            borderRadius: '6px',
                            border: '1px solid #ccc5b9',
                            cursor: 'pointer',
                            background: activeTab === tab ? '#353339' : '#fff',
                            color: activeTab === tab ? '#fff' : 'rgb(39,33,33)',
                            fontWeight: activeTab === tab ? 600 : 400,
                        }}
                    >
                        {tab === 'manage' ? '사용자 관리' : '사용자 권한 조회'}
                    </button>
                ))}
            </div>

            {/* 사용자 권한 조회 뷰 */}
            {activeTab === 'query' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    <Card>
                        <CardHeader>
                            <h2>사용자 권한 조회</h2>
                            <p>사용자를 검색하여 보유한 그룹과 권한을 확인합니다</p>
                        </CardHeader>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="사용자 검색 (이름, ID, 이메일, 부서명)"
                            value={querySearch}
                            onChange={e => { setQuerySearch(e.target.value); setQuerySelectedUser(null); setQueryUserGroups([]); setQuerySelectedGroupId(null); setQueryPermissions([]); }}
                            InputProps={{ startAdornment: <Search style={{ marginRight: '0.5rem', color: '#666' }} /> }}
                        />
                        {querySearch.trim() && (
                            <Box sx={{ mt: 1, border: '1px solid #e5e5e5', borderRadius: '4px', maxHeight: '200px', overflowY: 'auto' }}>
                                {queryFilteredUsers().length === 0 ? (
                                    <Box p={2}><Typography variant="body2" color="textSecondary">검색 결과가 없습니다</Typography></Box>
                                ) : queryFilteredUsers().map(u => (
                                    <UserItem key={u.user_id} onClick={() => handleQuerySelectUser(u)}
                                        style={{ background: querySelectedUser?.user_id === u.user_id ? 'rgba(204,197,185,0.2)' : undefined }}>
                                        <div>
                                            <Typography variant="body2" fontWeight={500}>{u.name || u.user_id}</Typography>
                                            <Typography variant="body2" color="textSecondary" fontSize="0.75rem">
                                                {u.email || u.user_id} {u.org_name ? `· ${u.org_name}` : ''} {u.posit_name ? `· ${u.posit_name}` : ''}
                                            </Typography>
                                        </div>
                                    </UserItem>
                                ))}
                            </Box>
                        )}
                    </Card>

                    {querySelectedUser && (
                        <div style={{ display: 'flex', gap: '1rem' }}>
                            {/* 사용자 그룹 목록 */}
                            <Card style={{ flex: 1 }}>
                                <CardHeader>
                                    <h2>{querySelectedUser.name || querySelectedUser.user_id}의 그룹</h2>
                                    <p>그룹을 선택하면 권한 정보가 표시됩니다</p>
                                </CardHeader>
                                {queryGroupsLoading ? (
                                    <Box display="flex" justifyContent="center" py={4}><CircularProgress size={32} /></Box>
                                ) : queryUserGroups.length === 0 ? (
                                    <EmptyState>
                                        <EmptyIcon><Check style={{ fontSize: '2rem', color: '#666' }} /></EmptyIcon>
                                        <Typography variant="body2" color="textSecondary">할당된 그룹이 없습니다</Typography>
                                    </EmptyState>
                                ) : (
                                    <Box sx={{ width: '100%', height: '300px' }}>
                                        <DataGridPremium
                                            rows={queryUserGroups.map(g => ({ id: g.group_id, group_id: g.group_id, group_nm: g.group_nm }))}
                                            columns={[
                                                { field: 'group_id', headerName: '그룹 ID', width: 100 },
                                                { field: 'group_nm', headerName: '그룹명', width: 200 },
                                                {
                                                    field: 'delete', headerName: '삭제', width: 60, sortable: false,
                                                    renderCell: p => (
                                                        <Button
                                                            onClick={e => { e.stopPropagation(); handleDeleteQueryUserGroup(p.row.group_id); }}
                                                            size="small"
                                                            color="error"
                                                            sx={{ minWidth: 'auto', padding: '4px 8px', height: '25px' }}
                                                        >
                                                            X
                                                        </Button>
                                                    )
                                                },
                                            ]}
                                            onRowClick={p => handleQuerySelectGroup(p.row.group_id)}
                                            disableRowSelectionOnClick
                                            hideFooter
                                            getRowHeight={() => 40}
                                            getRowClassName={p => p.row.group_id === querySelectedGroupId ? 'selected-row' : ''}
                                            sx={{
                                                '& .MuiDataGrid-cell:focus': { outline: 'none' },
                                                '& .MuiDataGrid-row:hover': { backgroundColor: 'rgba(3,2,19,0.05)', cursor: 'pointer' },
                                                '& .MuiDataGrid-row.selected-row': { backgroundColor: 'rgba(204,197,185,0.2) !important', borderLeft: '4px solid #ccc5b9' },
                                            }}
                                        />
                                    </Box>
                                )}
                            </Card>

                            {/* 권한 정보 */}
                            <Card style={{ flex: 1 }}>
                                <CardHeader>
                                    <h2>권한 정보</h2>
                                    <p>{querySelectedGroupId ? `그룹 ${querySelectedGroupId}의 권한` : '그룹을 선택하면 권한 정보가 표시됩니다'}</p>
                                </CardHeader>
                                {!querySelectedGroupId ? (
                                    <EmptyState>
                                        <EmptyIcon><Check style={{ fontSize: '2rem', color: '#666' }} /></EmptyIcon>
                                        <Typography variant="body2" color="textSecondary">그룹을 선택해주세요</Typography>
                                    </EmptyState>
                                ) : queryPermissionsLoading ? (
                                    <Box display="flex" justifyContent="center" py={4}><CircularProgress size={32} /></Box>
                                ) : queryPermissions.length === 0 ? (
                                    <EmptyState>
                                        <Typography variant="body2" color="textSecondary">권한이 없습니다</Typography>
                                    </EmptyState>
                                ) : (
                                    <Box sx={{ width: '100%', height: '300px' }}>
                                        <DataGridPremium
                                            rows={queryPermissions}
                                            columns={[
                                                { field: 'resource_nm', headerName: '리소스', flex: 1, sortable: true },
                                                {
                                                    field: 'action', headerName: '권한', width: 120,
                                                    renderCell: p => (
                                                        <span style={{ display: 'inline-flex', alignItems: 'center', padding: '0 8px', borderRadius: '6px', fontSize: '12px', height: '26px', backgroundColor: 'rgba(3,2,19,0.05)', color: 'rgb(39,33,33)' }}>
                                                            {p.value}
                                                        </span>
                                                    )
                                                },
                                            ]}
                                            hideFooter
                                            getRowHeight={() => 40}
                                            disableRowSelectionOnClick
                                            sx={{ '& .MuiDataGrid-cell:focus': { outline: 'none' } }}
                                        />
                                    </Box>
                                )}
                            </Card>
                        </div>
                    )}
                </div>
            )}

            {/* 사용자 관리 뷰 */}
            {activeTab === 'manage' && <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {/* Role 목록과 권한 정보 */}
                <div style={{ display: 'flex', gap: '1rem' }}>
                    {/* Role 목록 */}
                    <Card style={{ flex: 1 }}>
                        <CardHeader>
                            <h2>그룹 목록</h2>
                            <p>{groupsLoading ? "로딩 중..." : `총 ${groups.length}개의 그룹`}</p>
                        </CardHeader>
                        {groupsLoading ? (
                            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                                <CircularProgress size={32} />
                            </Box>
                        ) : (
                            <Box sx={{ width: "100%", height: "300px" }}>
                                <DataGridPremium
                                    rows={groups.map((group, index) => ({
                                        id: group.group_id,
                                        rownum: index + 1,
                                        group_id: group.group_id,
                                        group_nm: group.group_nm,
                                    }))}
                                    columns={[
                                        {
                                            field: "group_id",
                                            headerName: "그룹 ID",
                                            width: 120,
                                            flex: 0,
                                        },
                                        {
                                            field: "group_nm",
                                            headerName: "그룹명",
                                            flex: 1,
                                        },
                                    ]}
                                    loading={groupsLoading}
                                    onRowClick={(params) => handleGroupSelect(params.row.group_id)}
                                    disableRowSelectionOnClick
                                    hideFooter
                                    getRowHeight={() => 40}
                                    getRowClassName={(params) => {
                                        return params.row.group_id === selectedGroup ? "selected-row" : "";
                                    }}
                                    sx={{
                                        '& .MuiDataGrid-cell:focus': {
                                            outline: 'none'
                                        },
                                        '& .MuiDataGrid-cell:focus-within': {
                                            outline: 'none'
                                        },
                                        "& .MuiDataGrid-row:hover": {
                                            backgroundColor: "rgba(3, 2, 19, 0.05)",
                                            cursor: "pointer",
                                        },
                                        "& .MuiDataGrid-row.selected-row": {
                                            backgroundColor: "rgba(204, 197, 185, 0.2) !important",
                                            borderLeft: "4px solid #ccc5b9",
                                        },
                                    }}
                                />
                            </Box>
                        )}
                    </Card>

                    {/* 권한 정보 */}
                    <Card style={{ flex: 1 }}>
                        <CardHeader>
                            <h2>권한 정보</h2>
                            <p>{selectedGroup ? `그룹 ${selectedGroup}의 리소스별 권한` : "그룹을 선택하면 권한 정보가 표시됩니다"}</p>
                        </CardHeader>
                        {!selectedGroup ? (
                            <EmptyState>
                                <EmptyIcon>
                                    <Check style={{ fontSize: '2rem', color: '#666' }} />
                                </EmptyIcon>
                                <Typography variant="body2" color="textSecondary">
                                    그룹을 선택해주세요
                                </Typography>
                            </EmptyState>
                        ) : permissionsLoading ? (
                            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                                <CircularProgress size={32} />
                            </Box>
                        ) : permissions.length === 0 ? (
                            <EmptyState>
                                <Typography variant="body2" color="textSecondary">
                                    권한이 없습니다.
                                </Typography>
                            </EmptyState>
                        ) : (
                            <Box sx={{ width: "100%", height: "300px" }}>
                                <DataGridPremium
                                    rows={permissions}
                                    columns={[
                                        {
                                            field: "resource_nm",
                                            headerName: "리소스",
                                            width: 400,
                                            flex: 1,
                                            sortable: true,
                                        },
                                        {
                                            field: "action",
                                            headerName: "권한",
                                            width: 200,
                                            flex: 0,
                                            renderCell: (params) => (
                                                <div style={{ display: "flex", alignItems: "center", padding: "6px 0" }}>
                                                    <span
                                                        style={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            padding: "0px 8px",
                                                            borderRadius: "6px",
                                                            fontSize: "12px",
                                                            height: "26px",
                                                            backgroundColor: "rgba(3, 2, 19, 0.05)",
                                                            color: "rgb(39, 33, 33)",
                                                        }}
                                                    >
                                                        {params.value}
                                                    </span>
                                                </div>
                                            ),
                                        },
                                    ]}
                                    loading={permissionsLoading}
                                    hideFooter
                                    getRowHeight={() => 40}
                                    disableRowSelectionOnClick
                                    sx={{
                                        "& .MuiDataGrid-cell:focus": {
                                            outline: "none !important",
                                        },
                                        "& .MuiDataGrid-cell:focus-within": {
                                            outline: "none !important",
                                        },
                                    }}
                                />
                            </Box>
                        )}
                    </Card>
                </div>

                {/* 사용자 목록 */}
                <Card>
                    <CardHeader>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <h2 style={{ marginBottom: '8px' }}>사용자 관리</h2>
                                <p>
                                    {selectedGroup
                                        ? (usersLoading ? "로딩 중..." : `총 ${users.length}명의 사용자가 그룹 ${selectedGroup}에 할당되어 있습니다`)
                                        : "그룹을 선택하면 사용자 정보가 표시됩니다"}
                                </p>
                            </div>
                            {selectedGroup && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => setIsAddUserPanelOpen(!isAddUserPanelOpen)}
                                    startIcon={isAddUserPanelOpen ? <ExpandLess /> : <Add />}
                                    sx={{
                                        borderColor: '#ccc5b9',
                                        color: 'rgb(39, 33, 33)',
                                        '&:hover': {
                                            borderColor: '#b8b0a5',
                                            backgroundColor: 'rgba(204, 197, 185, 0.1)',
                                        }
                                    }}
                                >
                                    {isAddUserPanelOpen ? "접기" : "사용자 추가"}
                                </Button>
                            )}
                        </div>
                    </CardHeader>

                    {!selectedGroup ? (
                        <EmptyState>
                            <EmptyIcon>
                                <Check style={{ fontSize: '2rem', color: '#666' }} />
                            </EmptyIcon>
                            <Typography variant="body2" color="textSecondary">
                                그룹을 선택해주세요
                            </Typography>
                        </EmptyState>
                    ) : (
                        <>
                            {/* 인원 추가 폼 */}
                            <AddUserPanel isOpen={isAddUserPanelOpen}>
                                <AddUserContent isOpen={isAddUserPanelOpen}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="body2" fontWeight={500}>
                                            사용자 선택
                                        </Typography>
                                        {getFilteredUsers().length > 0 && (
                                            <Button
                                                size="small"
                                                onClick={toggleSelectAll}
                                                sx={{ 
                                                    textTransform: 'none', 
                                                    fontSize: '0.75rem',
                                                    color: 'rgb(39, 33, 33)',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(204, 197, 185, 0.1)',
                                                    }
                                                }}
                                            >
                                                {(() => {
                                                    const filtered = getFilteredUsers();
                                                    const allFilteredSelected = filtered.length > 0 && filtered.every((u) => selectedUserIds.includes(u.user_id));
                                                    return allFilteredSelected ? "전체 해제" : "전체 선택";
                                                })()}
                                            </Button>
                                        )}
                                    </div>

                                    <TextField
                                        fullWidth
                                        size="small"
                                        placeholder="사용자 검색 (이름, ID, 이메일, 직급, 조직명)"
                                        value={userSearchQuery}
                                        onChange={(e) => setUserSearchQuery(e.target.value)}
                                        InputProps={{
                                            startAdornment: <Search style={{ marginRight: '0.5rem', color: '#666' }} />,
                                        }}
                                    />

                                    {usersLoading ? (
                                        <Box display="flex" justifyContent="center" alignItems="center" py={2}>
                                            <CircularProgress size={24} />
                                        </Box>
                                    ) : availableUsers.length === 0 ? (
                                        <Box display="flex" justifyContent="center" alignItems="center" py={2}>
                                            <Typography variant="body2" color="textSecondary">
                                                추가할 사용자가 없습니다
                                            </Typography>
                                        </Box>
                                    ) : (() => {
                                        const filteredUsers = getFilteredUsers();
                                        return filteredUsers.length === 0 ? (
                                            <Box display="flex" justifyContent="center" alignItems="center" py={2}>
                                                <Typography variant="body2" color="textSecondary">
                                                    검색 결과가 없습니다
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <UserList>
                                                {filteredUsers.map((user) => (
                                                    <UserItem
                                                        key={user.user_id}
                                                        onClick={() => toggleUserSelection(user.user_id)}
                                                    >
                                                        <Checkbox
                                                            checked={selectedUserIds.includes(user.user_id)}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                toggleUserSelection(user.user_id);
                                                            }}
                                                        >
                                                            {selectedUserIds.includes(user.user_id) && (
                                                                <Check style={{ fontSize: '12px', color: 'white' }} />
                                                            )}
                                                        </Checkbox>
                                                        <div style={{ flex: 1, minWidth: 0 }}>
                                                            <Typography variant="body2" color="textSecondary" fontSize="0.75rem">
                                                                <span>{user.name || user.user_id} </span>
                                                                {user.email && (
                                                                    <span style={{ marginLeft: '0.5rem' }}>
                                                                        {user.org_name || user.posit_name ? `(${user.email})` : user.email}
                                                                    </span>
                                                                )}
                                                                <span> {user.posit_name || '-'}</span>
                                                                {user.org_name && <span> / {user.org_name}</span>}
                                                            </Typography>
                                                        </div>
                                                    </UserItem>
                                                ))}
                                            </UserList>
                                        );
                                    })()}

                                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <Button
                                            onClick={handleAddUser}
                                            disabled={selectedUserIds.length === 0 || isSubmitting || usersLoading}
                                            variant="contained"
                                            startIcon={<Add />}
                                            sx={{ 
                                                backgroundColor: '#ccc5b9',
                                                color: 'rgb(39, 33, 33)',
                                                '&:hover': {
                                                    backgroundColor: '#b8b0a5',
                                                },
                                                '&:disabled': {
                                                    backgroundColor: 'rgba(204, 197, 185, 0.3)',
                                                    color: 'rgba(39, 33, 33, 0.5)',
                                                }
                                            }}
                                        >
                                            {isSubmitting ? "추가 중..." : `추가 (${selectedUserIds.length}명)`}
                                        </Button>
                                    </div>
                                </AddUserContent>
                            </AddUserPanel>

                            {/* 사용자 테이블 */}
                            {usersLoading ? (
                                <Box display="flex" justifyContent="center" alignItems="center" py={3}>
                                    <CircularProgress size={32} />
                                </Box>
                            ) : users.length === 0 ? (
                                <EmptyState>
                                    <Typography variant="body2" color="textSecondary">
                                        인원이 없습니다.
                                    </Typography>
                                </EmptyState>
                            ) : (
                                <Box sx={{ height: 400, width: "100%" }}>
                                    <DataGridPremium
                                        rows={users.map((user) => ({
                                            id: user.user_id,
                                            user_id: user.user_id,
                                            name: user.name || "-",
                                            posit_name: user.posit_name || "-",
                                            email: user.email || "-",
                                            org_name: user.org_name || "-",
                                            create_dt: user.create_dt || "-",
                                        }))}
                                        columns={[
                                            {
                                                field: "user_id",
                                                headerName: "ID",
                                                width: 120,
                                                flex: 0,
                                            },
                                            {
                                                field: "name",
                                                headerName: "이름",
                                                width: 120,
                                                flex: 0,
                                            },
                                            {
                                                field: "posit_name",
                                                headerName: "직급",
                                                width: 100,
                                                flex: 0,
                                            },
                                            {
                                                field: "email",
                                                headerName: "이메일",
                                                width: 200,
                                                flex: 0,
                                            },
                                            {
                                                field: "org_name",
                                                headerName: "부서명",
                                                width: 180,
                                                flex: 0,
                                            },
                                            {
                                                field: "create_dt",
                                                headerName: "생성일시",
                                                width: 150,
                                                flex: 0,
                                                renderCell: (params) => (
                                                    <Typography variant="body2" color="textSecondary" fontSize="0.875rem">
                                                        {formatDate(params.value)}
                                                    </Typography>
                                                ),
                                            },
                                            {
                                                field: "delete",
                                                headerName: "삭제",
                                                width: 100,
                                                flex: 0,
                                                align: "center",
                                                headerAlign: "center",
                                                sortable: false,
                                                renderCell: (params) => (
                                                    <Button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeleteUser(params.row.user_id);
                                                        }}
                                                        size="small"
                                                        color="error"
                                                        sx={{
                                                            minWidth: 'auto',
                                                            padding: '4px 8px',
                                                            height: '25px',
                                                        }}
                                                    >
                                                        X
                                                    </Button>
                                                ),
                                            },
                                        ]}
                                        loading={usersLoading}
                                        hideFooter
                                        getRowHeight={() => 40}
                                        disableRowSelectionOnClick
                                        sx={{
                                            "& .MuiDataGrid-cell:focus": {
                                                outline: "none !important",
                                            },
                                            "& .MuiDataGrid-cell:focus-within": {
                                                outline: "none !important",
                                            },
                                        }}
                                    />
                                </Box>
                            )}
                        </>
                    )}
                </Card>
            </div>}
        </Container>
    );
}

